import React, { useEffect, useState } from 'react';
import { DeleteForever, Edit, IosShare, Search, ContentCopy } from '@mui/icons-material';
import { useTheme, Paper, Box, Typography, IconButton, Tooltip, Stack } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { Query } from 'system/query/Query';
import QueryViewer from '../QueryViewer';
import ISODateTime from 'components/formatting/ISODateTime';
import { QueryInfo } from 'system/query/QueryInfo';
import APIRequest, { APIError } from 'system/APIRequest';
import Chip from 'components/uiElements/dataDisplay/Chip';
import { Tag } from 'routes/tagManagement/TagManagementPage';
import TagRequestHelper from 'routes/tagManagement/TagRequestHelper';
import QueryDialog from '../QueryDialog';
import LoadDashboardDialog from 'routes/queryManagement/LoadDashboardDialog';
import { ResourceActionDialog } from 'components/adminResources/ResourceActionDialog';
import CopyQueryDialog from 'routes/queryManagement/CopyQueryDialog';

export interface SavedQueryRowProps {
  buttonSize?: "large" | "medium" | "small";
  canDelete?: boolean;
  canEdit?: boolean;

  isSelected?: boolean;
  loadOwnQueryInfo?: boolean;
  onChange?(queryId: string): void;
  onClick?(): void;
  onCopyCreated?(queryId: string): void;
  onDelete?: (query: Query) => void;
  queryInfo?: QueryInfo;
  savedQuery: Query;
  showCopyButton?: boolean;
  showDeleteButton?: boolean;
  showEditButton?: boolean;
  showLoadQueryButton?: boolean;
  showShareButton?: boolean;
}

export default function SavedQueryRow(props: SavedQueryRowProps): JSX.Element {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [queryInfo, setQueryInfo] = useState<QueryInfo | undefined>(props.queryInfo);
  const [tags, setTags] = useState<Array<Tag>>([]);
  const [showEditDialog, setShowEditDialog] = useState<boolean>(false);
  const [showLoadQueryDialog, setShowLoadQueryDialog] = useState<boolean>(false);
  const [showShareDialog, setShowShareDialog] = useState<boolean>(false);
  const [showCopyQueryDialog, setShowCopyQueryDialog] = useState<boolean>(false);
  const [queryToEdit, setQueryToEdit] = useState<Query | null>(null);

  useEffect(() => {
    if (!props.savedQuery?.dataset_id) {
      return;
    }

    if (!props.loadOwnQueryInfo || props.queryInfo) {
      setQueryInfo(props.queryInfo);
      return;
    }

    APIRequest(`query-info/${props.savedQuery.dataset_id}`).then((response: any) => {
      setQueryInfo(response);
    }).catch((reason: APIError) => {
      enqueueSnackbar(`${i18n.t('query-form.error-loading-query-info')}: ${reason.message}`, {variant: 'error', persist: true});
    });
  }, [props.queryInfo, props.savedQuery.dataset_id, props.loadOwnQueryInfo]);

  async function loadTags() {
    if (props.savedQuery.id) {
      const result = await TagRequestHelper.getTagsForQuery(props.savedQuery.id);
      setTags(result.tags);
    }
  }

  useEffect(() => {
    if (!props.tags) {
      loadTags();
    }
  }, [props.savedQuery.id]);

  function handleClick() {
    if (props.canEdit) {
      setShowEditDialog(true);
    }
    if (props.onClick) {
      props.onClick();
    }
  }
  const queryColor = props.savedQuery.meta?.color ? props.savedQuery.meta.color : "transparent";
  const borderColor = props.isSelected ? theme.palette.primary.main : "transparent";
  const disableCursor = !props.canEdit && !props.onClick;

  return (
    <Paper sx={{
      position: "relative",
      cursor: disableCursor ? "" : "pointer",
      border: props.isSelected ? `2px solid ${borderColor}` : "none",
      backgroundColor: props.isSelected ? theme.palette.action.selected : "",
      "&:hover": {
        backgroundColor: disableCursor ? "" : theme.palette.action.hover,
      }}}
      onClick={handleClick}
     >
      <Box sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "250px 1fr" },
        gridTemplateRows: "1fr",
        borderLeft: "6px solid " + queryColor,
        borderRadius: theme.general.borderRadiusSm
      }}>
        <Box sx={{
          padding: "8px",
          display: "flex",
          flexDirection: "column",
          flexBasis: "100%",
          flex: 1 }}>
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              gap: 1
            }}>
            <Typography variant="h4" sx={{p: 0, m:0}}>
              {props.savedQuery.name}
            </Typography>
          </Stack>
          <Typography>
            {props.savedQuery?._user?.name}
          </Typography>
          <Typography sx={{ color: theme.palette.text.secondary }}>
            {props.savedQuery.created_at &&
              <ISODateTime isoString={props.savedQuery.created_at} />
            }
          </Typography>
          {tags.length > 0 &&
            <Stack
              direction="row"
              sx={{
                gap: 0.5,
                flexWrap: "wrap"
              }}>
              {tags?.map((tag, index) => {
                return <Chip color={tag.color} key={`SavedQueryRow.${index}`} label={tag.name} />;
              })}
            </Stack>
          }
        </Box>
        <Box sx={{ maxHeight: "175px", overflowY: "auto" }}>
          {queryInfo &&
            <QueryViewer query={props.savedQuery} queryInfo={queryInfo}/>
          }
        </Box>
      </Box>
      <Stack
        direction="row"
        sx={{
          gap: 0.5,
          position: "absolute",
          top: "4px",
          right: "4px",
          zIndex: 999
        }}>
        {props.showLoadQueryButton &&
          <Tooltip title={i18n.t("saved-query-row.button-load-in-dashboard")} followCursor={true}>
            <span>
              <IconButton
                size={props.buttonSize ?? undefined}
                sx={{
                  backgroundColor: theme.palette.background.paper, 
                  opacity: 0.6,
                  "&:hover": { opacity: 1 }
                }}
                onClick={(event: React.MouseEvent) => {
                  event.stopPropagation();
                  event.preventDefault();
                  setShowLoadQueryDialog(true);
                }}>
                <Search/>
              </IconButton>
            </span>
          </Tooltip>
        }
        {props.showShareButton &&
          <Tooltip title={i18n.t("saved-query-row.button-share-options")} followCursor={true}>
            <span>
              <IconButton
                size={props.buttonSize ?? undefined}
                sx={{
                  backgroundColor: theme.palette.background.paper, 
                  opacity: 0.6,
                  "&:hover": { opacity: 1 }
                }}
                onClick={(event: React.MouseEvent) => {
                  event.stopPropagation();
                  event.preventDefault();
                  setShowShareDialog(true);
                }}>
                <IosShare />
              </IconButton>
            </span>
          </Tooltip>
        }
        {props.showCopyButton &&
          <Tooltip title={i18n.t("copy")} followCursor={true}>
            <span>
              <IconButton
                size={props.buttonSize ?? undefined}
                sx={{
                  backgroundColor: theme.palette.background.paper, 
                  opacity: 0.6,
                  "&:hover": { opacity: 1 }
                }}
                onClick={(event: React.MouseEvent) => {
                  event.stopPropagation();
                  event.preventDefault();
                  setShowCopyQueryDialog(true);
                }}>
                <ContentCopy />
              </IconButton>
            </span>
          </Tooltip>
        }
        {props.showEditButton &&
          <Tooltip title={i18n.t('widget-types.template-search.edit-query-in-builder')} followCursor={true}>
            <span>
              <IconButton
                size={props.buttonSize ?? undefined}
                sx={{
                  backgroundColor: theme.palette.background.paper, 
                  opacity: 0.6,
                  "&:hover": { opacity: 1 }
                }}
                onClick={() => {
                  if (props.canEdit) {
                    setShowEditDialog(true);
                  }
                }}>
                <Edit />
              </IconButton>
            </span>
          </Tooltip>
        }
        {props.showDeleteButton &&
          <Tooltip title={i18n.t('delete')} followCursor={true}>
            <span>
              <IconButton
                color='error'
                size={props.buttonSize ?? undefined}
                sx={{
                  backgroundColor: theme.palette.background.paper, 
                  opacity: 0.6,
                  "&:hover": { opacity: 1 }
                }}
                disabled={!props.canDelete}
                onClick={(event: React.MouseEvent) => {
                  event.stopPropagation();
                  event.preventDefault();
                  if (props.onDelete) {
                    props.onDelete(props.savedQuery);
                  }
                }}>
                <DeleteForever />
              </IconButton>
            </span>
          </Tooltip>
        }
      </Stack>
      {showEditDialog &&
        <QueryDialog
          isOpen={true}
          query={queryToEdit || props.savedQuery}
          onClose={() => {
            setShowEditDialog(false);
            if (queryToEdit) {
              setQueryToEdit(null);
            }
          }}
          onSubmit={(queryId) => {
            setShowEditDialog(false);
            if (queryToEdit) {
              setQueryToEdit(null);
              if (props.onCopyCreated) {
                props.onCopyCreated(queryId);
              }
            } else {
              loadTags();
              if (props.onChange) {
                props.onChange(queryId);
              }
            }
          }}
        />
      }
      {showLoadQueryDialog &&
        <div onClick={(event) => {
          // Prevent clicks in the LoadDashboardDialog from bubbling.
          event.stopPropagation();
        }}>
          <LoadDashboardDialog
            onClose={() => setShowLoadQueryDialog(false)}
            open={showLoadQueryDialog}
            query={props.savedQuery} />
        </div>
      }
      {showShareDialog &&
        <ResourceActionDialog
          open={showShareDialog}
          accountAccess={props.savedQuery.access}
          action="share"
          resourceIds={props.savedQuery.id as string}
          endpoint="query"
          onClose={() => setShowShareDialog(false) }
          handleComplete={(wasSuccessful: boolean) => {
            if (wasSuccessful) {
              setShowShareDialog(false);
              if (props.onChange && props.savedQuery.id) {
                props.onChange(props.savedQuery.id);
              }
              enqueueSnackbar(i18n.t("resource-editor.save-successful"), { variant: "success" });
            }
          }}
        />
      }
      {showCopyQueryDialog &&
        <div onClick={(event) => {
          // Prevent clicks in the LoadDashboardDialog from bubbling.
          event.stopPropagation();
        }}>
          <CopyQueryDialog
            onClose={() => {
              setShowCopyQueryDialog(false);
              setQueryToEdit(null);
            }}
            onComplete={(queryId) => {
              setShowCopyQueryDialog(false);
              if (props.onChange) {
                props.onChange(queryId);
              }
            }}
            onCopyAndEdit={(query: Query) => {
              setShowCopyQueryDialog(false);
              setQueryToEdit(query);
              setShowEditDialog(true);
            }}
            open={showCopyQueryDialog}
            query={props.savedQuery}
          />
        </div>
      }
    </Paper>
  );
}
